import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AiInterviewState, ConversationPayload, EndSessionPayload, InterviewSessionPayload, InterviewSessionResponse } from '@edtech-ui/core/models/ai-interview/ai-interview-session';
import { Interviewer } from '@edtech-ui/core/models/ai-interview/ai-interviwer';
import { ApiBaseResponse } from '@edtech-ui/core/models/api-base-model';
import { environment } from 'environments/environment';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, catchError, Observable, retry, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AiInterviewSessionService {
  private baseUrl = environment.aiInterviewManagementUrl;

  constructor(private readonly http: HttpClient, private toast: ToastrService) { }

  private state$ = new BehaviorSubject<AiInterviewState>(this.load());
  readonly stateChanges$ = this.state$.asObservable();

  get value(): AiInterviewState {
    return this.state$.value;
  }

  update(patch: Partial<AiInterviewState>) {
    const updated = { ...this.value, ...patch };
    this.state$.next(updated);
    localStorage.setItem('ai_session_state', JSON.stringify(updated));
  }

  reset() {
    localStorage.removeItem('ai_session_state');
    this.state$.next({});
  }

  private load(): AiInterviewState {
    try {
      const raw = localStorage.getItem('ai_session_state');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  loadFromStorage() {
    const raw = localStorage.getItem('ai_session_state');
    return raw ? JSON.parse(raw) : {};
  }

  getInterviewers() {
    const url = `${this.baseUrl}/interviewers`;
    return this.http.get<{ Data: Interviewer[]; Success: boolean; Message: string }>(url);
  }

  getLanguages(): Observable<{ Data: any[]; Success: boolean; Message: string }> {
    const url = `${this.baseUrl}/realtime-interview/languages`;
    return this.http.get<{ Data: any[]; Success: boolean; Message: string }>(url);
  }

  createInterviewSession(payload: InterviewSessionPayload): Observable<InterviewSessionResponse> {
    const formData = new FormData();

    Object.entries(payload).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        if (value instanceof File) formData.append(key, value);
        else formData.append(key, value.toString());
      }
    });

    return this.http.post<InterviewSessionResponse>(`${this.baseUrl}/realtime-interview/session`, formData).pipe(
      retry({ count: 2, delay: 1000 }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  updateConversation(payload: {
    session_id: string;
    conversation_json: { role: string; content: string; timestamp: string }[];
  }): Observable<ApiBaseResponse> {
    const formData = new FormData();
    formData.append('session_id', payload.session_id);
    formData.append('conversation_json', JSON.stringify(payload.conversation_json));


    return this.http.post<ApiBaseResponse>(
      `${this.baseUrl}/realtime-interview/update-conversation`,
      formData
    );
  }

  endInterview(payload: { session_id: string }) {
    const formData = new FormData();
    formData.append('session_id', payload.session_id);
    return this.http.post<ApiBaseResponse>(
      `${this.baseUrl}/realtime-interview/end`,
      formData
    );
  }

  evaluateInterview(session_id: string) {
    const url = `${this.baseUrl}/realtime-interview/evaluate`;
    const formData = new FormData();
    formData.append('session_id', session_id);

    return this.http.post<ApiBaseResponse>(url, formData).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }
}
